import React, { useEffect, useState, useCallback, useContext } from 'react';
import './GatewaysTab.css';
import { Dropdown } from '@fluentui/react';
import { getGatewayHealth, getGatewayHeartbeats, getGatewayStrip, setGatewayActive } from "../../service/GatewayHealthService";
import { PrimaryButton } from '@fluentui/react';
import CustomToolTipHost from '../../components/CustomToolTipHost/CustomToolTipHost';
import { superAdminReAssignButtonStyle } from '../StudyList/helpers/initData';
import ModalMain from '../../components/Modal/ModalMain';
import ModalHeader from '../../components/Modal/ModalHeader';
import ModalFooter from '../../components/Modal/ModalFooter';
import { useTranslation } from 'react-i18next';
import themeContext from '../../context/theme/ThemeContext';
import loaderContext from '../../context/loader/LoaderContext';
import {
  formatTime,
  formatDateTime,
  heartbeatPillClass,
  STATUS_TO_PILL,
  PILL_BG,
  PILL_TEXT,
  STATUS_TO_LABEL,
  CATEGORY_LEVEL_LABEL,
  HEARTBEAT_PAGE_SIZE,
  intervalDropdownStyles,
  pagerButtonStyle,
  actionButtonStyle,
} from './Helper';

function CategoryPill({ category, intervalHours }) {
  const level = category.lastStatus || 'NO_DATA';
  const showCounts = category.criticalCount > 0 || category.warningCount > 0;
  const label = STATUS_TO_LABEL[level] || level;
  const shortLabel = CATEGORY_LEVEL_LABEL[level] || label;
  const pillId = category.key || shortLabel;

  return (
    <span className="cat-pill">
      <CustomToolTipHost content={label} id={pillId + "-status"}>
        <span className={'cat-pill-level' + (showCounts ? '' : ' cat-pill-level-only')} style={{ backgroundColor: PILL_BG[level] || PILL_BG.NO_DATA, color: PILL_TEXT[level] || PILL_TEXT.NO_DATA }}>{shortLabel}</span>
      </CustomToolTipHost>
      {showCounts && (
        <span className="cat-pill-counts">
          <CustomToolTipHost content={"Last " + intervalHours + " hours"} id={pillId + "-hours"}>
            <span>{intervalHours}h</span>
          </CustomToolTipHost>
          <span className="cat-pill-divider" />
          {category.criticalCount > 0 && (
            <CustomToolTipHost content={"Critical: " + category.criticalCount} id={pillId + "-critical"}>
              <span><span className="cat-pill-dot" style={{ color: PILL_BG.CRITICAL }}>{'\u25CF'}</span>{category.criticalCount}</span>
            </CustomToolTipHost>
          )}
          {category.warningCount > 0 && (
            <CustomToolTipHost content={"Warning: " + category.warningCount} id={pillId + "-warning"}>
              <span><span className="cat-pill-dot" style={{ color: PILL_BG.WARNING }}>{'\u25CF'}</span>{category.warningCount}</span>
            </CustomToolTipHost>
          )}
        </span>
      )}
    </span>
  );
}

function HeartbeatStrip({ slots }) {
  if (!slots) return null;
  return (
    <>
      <div className="strip-label">
        24-Hour Heartbeat Strip ({slots.length} blocks, 15 min each) &mdash; colored by that heartbeat's own worst status
        &nbsp;&middot;&nbsp; Green = OK &nbsp; Amber = Warning &nbsp; Red = Critical &nbsp; Grey = No data (heartbeat missing)
      </div>
      <div className="strip">
        {slots.map((s, i) => (
          <CustomToolTipHost
            key={i}
            content={formatTime(s.slotStart)}
            id={"hb-strip-" + i}
          >
            <div className={'blk ' + (STATUS_TO_PILL[s.status] === 'gray' ? 'nodata' :
              s.status === 'OK' ? 'ok' : s.status === 'WARNING' ? 'warn' : 'crit')} />
          </CustomToolTipHost>
        ))}
      </div>
    </>
  );
}

function JsonPayloadModal({ payload, onClose }) {
  const [t] = useTranslation();
  const DefaultButtonList = [
    {
      title: t("button.close"),
      onclick: onClose,
    },
  ];
  const modalDetails = {
    header: (
      <ModalHeader
        updatedModal={true}
        heading="Heartbeat JSON"
        handleCancel={onClose}
      />
    ),
    body: <JsonPayloadModalBody payload={payload} />,
    footer: (
      <ModalFooter
        PrimaryButtonList={[]}
        DefaultButtonList={DefaultButtonList}
      />
    ),
    width: "640px",
  };

  return (
    <>
      <ModalMain
        updatedModal={true}
        modalDetails={modalDetails}
        showModal={true}
      />
    </>
  );
}

function JsonPayloadModalBody({ payload }) {
  const { getThemeColor } = useContext(themeContext);
  return (
    <pre
      style={{
        margin: 0,
        padding: "16px 20px",
        fontFamily: "Open Sans",
        fontSize: "12px",
        fontWeight: "400",
        color: getThemeColor("primary-color"),
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxHeight: "50vh",
        overflow: "auto",
      }}
    >
      {JSON.stringify(payload, null, 2)}
    </pre>
  );
}

function JsonRow({ payload }) {
  const [open, setOpen] = useState(false);
  if (payload == null) return <span className="nodata-text">&mdash;</span>;
  return (
    <>
      <span
        className="json-link"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        View JSON
      </span>
      {open && <JsonPayloadModal payload={payload} onClose={() => setOpen(false)} />}
    </>
  );
}

function GatewayDetail({ gatewayId, appVersion, intervalHours }) {
  const [strip, setStrip] = useState(null);
  const [page, setPage] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStrip = useCallback(async () => {
    const response = await getGatewayStrip(gatewayId, intervalHours, appVersion);
    if (response.status === 200) {
      setStrip(response.data.data);
    } else {
      setStrip([]);
    }
  }, [gatewayId, intervalHours, appVersion]);

  const loadPage = useCallback(async (p) => {
    setLoading(true);
    const res = await getGatewayHeartbeats(gatewayId, intervalHours, p, HEARTBEAT_PAGE_SIZE, appVersion);
    if (res.status === 200) {
        setPageData(res.data.data);
        setLoading(false);
    } else {
        setPageData(null);
        setLoading(false);
    }
    
  }, [gatewayId, intervalHours, appVersion]);

  useEffect(() => {
    setPageData(null);
    loadStrip();
    setPage(0);
    loadPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayId, intervalHours, appVersion]);

  const goPrev = () => { const p = Math.max(0, page - 1); setPage(p); loadPage(p); };
  const goNext = () => {
    if (pageData && page < pageData.totalPages - 1) { const p = page + 1; setPage(p); loadPage(p); }
  };

  return (
    <div className="detail-body">
      <HeartbeatStrip slots={strip} />
      <div className="detail-note">
        Heartbeat details &mdash; most recent first. "View JSON" is the literal payload the gateway sent for that heartbeat.
      </div>

      <div className="raw-wrap">
        <div className={"raw-scroll" + (loading ? " is-loading" : "")}>
          <table className="raw">
            <colgroup>
              <col style={{ width: "11%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Time</th><th>Modality</th><th>Queue (Pend / Done / Oldest)</th><th>Local Disk</th>
                <th>Cloud Storage</th><th>Hardware (CPU / Mem / Disk)</th><th>App Uptime</th><th>Raw Payload</th>
              </tr>
            </thead>
            <tbody>
              {(!pageData || pageData.content.length === 0) && (
                <tr>
                  <td colSpan={8} className="raw-empty">
                    {loading ? "Loading\u2026" : "No heartbeats received in the selected window."}
                  </td>
                </tr>
              )}
              {pageData && pageData.content.map((r, i) => (
                <tr key={i}>
                  <td>{formatTime(r.time)}</td>
                  <td><span className={STATUS_TO_PILL[r.modality.severity] === 'red' ? 'crit-text'
                    : STATUS_TO_PILL[r.modality.severity] === 'amber' ? 'warn-text'
                    : STATUS_TO_PILL[r.modality.severity] === 'gray' ? 'nodata-text' : 'ok-text'}>{r.modality.text}</span></td>
                  <td><span className={STATUS_TO_PILL[r.queue.severity] === 'red' ? 'crit-text'
                    : STATUS_TO_PILL[r.queue.severity] === 'amber' ? 'warn-text'
                    : STATUS_TO_PILL[r.queue.severity] === 'gray' ? 'nodata-text' : 'ok-text'}>{r.queue.text}</span></td>
                  <td><span className={STATUS_TO_PILL[r.localDisk.severity] === 'red' ? 'crit-text'
                    : STATUS_TO_PILL[r.localDisk.severity] === 'amber' ? 'warn-text'
                    : STATUS_TO_PILL[r.localDisk.severity] === 'gray' ? 'nodata-text' : 'ok-text'}>{r.localDisk.text}</span></td>
                  <td><span className={STATUS_TO_PILL[r.cloudStorage.severity] === 'red' ? 'crit-text'
                    : STATUS_TO_PILL[r.cloudStorage.severity] === 'gray' ? 'nodata-text' : 'ok-text'}>{r.cloudStorage.text}</span></td>
                  <td>{r.hardwareText || '\u2014'}</td>
                  <td>{r.appUptime || '\u2014'}</td>
                  <td><JsonRow payload={r.rawPayload} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pager">
          <PrimaryButton
            text={"\u2039 Prev"}
            onClick={goPrev}
            disabled={loading || page === 0}
            styles={pagerButtonStyle}
          />
          <span>
            {pageData
              ? "Page " + (page + 1) + " of " + pageData.totalPages + " \u00b7 Showing " +
                (pageData.totalElements === 0 ? 0 : page * HEARTBEAT_PAGE_SIZE + 1) +
                "\u2013" + Math.min((page + 1) * HEARTBEAT_PAGE_SIZE, pageData.totalElements) +
                " of " + pageData.totalElements
              : loading ? "Loading\u2026" : ""}
          </span>
          <PrimaryButton
            text={"Next \u203a"}
            onClick={goNext}
            disabled={loading || !pageData || page >= pageData.totalPages - 1}
            styles={pagerButtonStyle}
          />
        </div>
      </div>
    </div>
  );
}

export default function GatewaysTab() {
  const [intervalHours, setIntervalHours] = useState(24);
  const [gatewayStatus, setGatewayStatus] = useState("active");
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const { ref, setLoaderRunning } = useContext(loaderContext);
  const [error, setError] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null); // only one open at a time; set to a Set for multi
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    setLoaderRunning(true);
    setError(null);
    const response = await getGatewayHealth(intervalHours, gatewayStatus);
    if(response.status === 200) {
        console.log("Gateway Health Response:", response);

      setData(response.data.data);
      setLastUpdated(new Date());
      setLoaderRunning(false);
    }else {
      setError('Failed to fetch gateway health data');
      setLoaderRunning(false);
    }
  }, [intervalHours, gatewayStatus]);

  useEffect(() => { load(); }, [load]);

  const toggleGateway = (key) => setExpandedKey(expandedKey === key ? null : key);

  const clients = (data?.clients || []).filter((c) => {
    if (search && !c.clientName.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter === 'ALL') return true;
    return c.gateways.some((g) => g.categories.some((cat) => cat.lastStatus === statusFilter)
      || heartbeatPillClass(g.heartbeatPercent) === (statusFilter === 'CRITICAL' ? 'red' : statusFilter === 'WARNING' ? 'amber' : 'green'));
  });

  const totalGateways = clients.reduce((sum, c) => sum + c.gateways.length, 0);

  return (
    <div className="gateways-tab">
      <div className="toolbar">
        {/* <div className="search-box">
          <svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" /><path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          <input placeholder="Search clients" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div> */}
        <Dropdown
          options={[
            { key: "24", text: "Last 24 Hours" },
            { key: "12", text: "Last 12 Hours" },
            { key: "6", text: "Last 6 Hours" },
            { key: "3", text: "Last 3 Hours" },
          ]}
          onChange={(e, option) => setIntervalHours(Number(option?.key))}
          defaultSelectedKey={intervalHours.toString()}
          className="interval-dropdown"
          styles={intervalDropdownStyles}
        />
        <Dropdown
          options={[
            { key: "active", text: "Active" },
            { key: "inactive", text: "Inactive" },
          ]}
          selectedKey={gatewayStatus}
          onChange={(e, option) => setGatewayStatus(option?.key || "active")}
          className="interval-dropdown"
          styles={intervalDropdownStyles}
        />

        {/* <select className="ctrl" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="ALL">All statuses</option>
          <option value="CRITICAL">Critical</option>
          <option value="WARNING">Warning</option>
          <option value="OK">Healthy</option>
        </select> */}
        <span className="count-text">
          Clients: {clients.length} &middot; Gateways: {totalGateways}
        </span>
        <div className="spacer" />
        <span className="updated-text">
          {lastUpdated
            ? "Last updated: " +
              lastUpdated.toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })
            : ""}
        </span>
        <CustomToolTipHost content={"Refresh"}>
          <PrimaryButton
            iconProps={{
              iconName: "Refresh",
              cursor: "pointer",
            }}
            onClick={() => load()}
            styles={superAdminReAssignButtonStyle}
          />
        </CustomToolTipHost>
      </div>

      {error && (
        <div className="detail-note" style={{ color: "var(--red)" }}>
          Failed to load: {error}
        </div>
      )}
      {/* {loading && !data && (
        <div className="detail-note">Loading gateways&hellip;</div>
      )} */}

      {data && (
        <div className="table-scroll">
        <table>
          <colgroup>
            <col className="col-client" />
            <col className="col-gateway" />
            <col className="col-heartbeat" />
            <col className="col-queue" />
            <col className="col-disk" />
            <col className="col-cloud" />
            <col className="col-hardware" />
            <col className="col-modality" />
            <col className="col-action" />
          </colgroup>
          <thead className="list-view-thead">
            <tr>
              <th>Client</th>
              <th>Gateway</th>
              <th>Heartbeat</th>
              <th>Queue</th>
              <th>Disk</th>
              <th>Cloud Storage</th>
              <th>Hardware</th>
              <th>Modality Conn.</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client, clientIdx) =>
              client.gateways.map((gw, idx) => {
                const key = client.clientId + "-" + gw.gatewayId + "-" + (gw.appVersion || "");
                const expandedInClient = client.gateways.some(
                  (g) => expandedKey === client.clientId + "-" + g.gatewayId + "-" + (g.appVersion || "")
                );
                return (
                  <GatewayRow
                    key={key}
                    client={client}
                    gateway={gw}
                    clientRowSpan={idx === 0 ? client.gateways.length + (expandedInClient ? 1 : 0) : 0}
                    isSibling={client.gateways.length > 1}
                    isFirstRow={clientIdx === 0 && idx === 0}
                    expanded={expandedKey === key}
                    onToggle={() => toggleGateway(key)}
                    intervalHours={intervalHours}
                    onStatusChange={load}
                  />
                );
              }),
            )}
            {clients.length === 0 && (
              <tr className="list-view-border">
                <td
                  colSpan={9}
                  style={{ textAlign: "center", padding: "24px" }}
                >
                  No clients match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      )}

      <div className="legend">
        <span>Metrics above reflect the last {intervalHours} hours</span>
        <span>
          <span className="dot green" />
          OK
        </span>
        <span>
          <span className="dot amber" />
          Warning
        </span>
        <span>
          <span className="dot red" />
          Critical
        </span>
        <span>
          <span className="dot gray" />
          No data
        </span>
      </div>
    </div>
  );
}

// One gateway row. showClientName is true only for a client's first gateway — every row still
// gets its own client-column cell (left blank otherwise) rather than using rowSpan, since rowSpan
// counts physical <tr> elements and would land on the wrong row once a detail row is inserted
// between a client's gateway rows.
function GatewayRow({ client, gateway, clientRowSpan, isSibling, isFirstRow, expanded, onToggle, intervalHours, onStatusChange }) {
  return (
    <>
      <tr className={"clickable" + (isSibling ? " gw-sibling" : "") + (isFirstRow ? "" : " list-view-border")} onClick={onToggle}>
        {clientRowSpan > 0 && (
          <td className="client-cell" rowSpan={clientRowSpan}>
            <CustomToolTipHost content={client.clientName} id={"client-name-" + client.clientId}>
              <span className="client-name">{client.clientName}</span>
            </CustomToolTipHost>
          </td>
        )}
        <GatewayCells gateway={gateway} expanded={expanded} clientId={client.clientId} intervalHours={intervalHours} onStatusChange={onStatusChange} />
      </tr>
      {expanded && (
        <tr className="detail-row list-view-border">
          <td colSpan={8}>
            <div className="detail-head">
              <div><b>Gateway {gateway.gatewayId}</b> &middot; {client.clientName}</div>
              <div className="gwmeta">Last heartbeat: {formatDateTime(gateway.lastHeartbeatAt)}</div>
            </div>
            <GatewayDetail gatewayId={gateway.gatewayId} appVersion={gateway.appVersion} intervalHours={intervalHours} />
          </td>
        </tr>
      )}
    </>
  );
}

function GatewayStatusModal({ gateway, clientId, onClose, onSaved }) {
  const [t] = useTranslation();
  const { getThemeColor } = useContext(themeContext);
  const nextActive = gateway.active === false;

  const confirm = async () => {
    const response = await setGatewayActive(clientId, gateway.gatewayId, gateway.appVersion, nextActive);
    if (response && response.status === 200 && response.data && response.data.result === "SUCCESS") {
      onSaved();
      return;
    }
    onClose();
  };

  const modalDetails = {
    header: (
      <ModalHeader
        updatedModal={true}
        heading={nextActive ? "Mark gateway active" : "Mark gateway inactive"}
        handleCancel={onClose}
      />
    ),
    body: (
      <div
        style={{
          padding: "16px 20px",
          fontFamily: "Open Sans",
          fontSize: "14px",
          color: getThemeColor("primary-color"),
        }}
      >
        {nextActive
          ? "This gateway will be included in the dashboard and alert emails."
          : "This gateway will be excluded from the dashboard and alert emails."}
      </div>
    ),
    footer: (
      <ModalFooter
        PrimaryButtonList={[{ title: t("button.yes"), onclick: confirm }]}
        DefaultButtonList={[{ title: t("button.no"), onclick: onClose }]}
      />
    ),
    width: "480px",
  };

  return (
    <ModalMain updatedModal={true} modalDetails={modalDetails} showModal={true} />
  );
}

function GatewayCells({ gateway, expanded, clientId, intervalHours, onStatusChange }) {
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  const openStatusModal = (event) => {
    event.stopPropagation();
    setStatusModalOpen(true);
  };

  return (
    <>
      <td className="gw-cell">
        <svg className={'chevron' + (expanded ? ' open' : '')} viewBox="0 0 24 24" fill="none">
          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="mono" title={gateway.gatewayId}>{gateway.gatewayId}</span>
        <span className="ver">App {gateway.appVersion || 'unknown'}</span>
      </td>
      <td className="hb-cell">
        <span className={'pill ' + heartbeatPillClass(gateway.heartbeatPercent)}>
          {gateway.heartbeatsReceived} / {gateway.heartbeatsExpected} recv.
        </span>
      </td>
      {gateway.categories.map((c) => <td key={c.key} className="cat-cell"><CategoryPill category={c} intervalHours={intervalHours} /></td>)}
      <td onClick={(event) => event.stopPropagation()}>
        <PrimaryButton
          text={gateway.active === false ? "Activate" : "Deactivate"}
          onClick={openStatusModal}
          styles={actionButtonStyle}
        />
        {statusModalOpen && (
          <GatewayStatusModal
            gateway={gateway}
            clientId={clientId}
            onClose={() => setStatusModalOpen(false)}
            onSaved={() => {
              setStatusModalOpen(false);
              onStatusChange();
            }}
          />
        )}
      </td>
    </>
  );
}
